import { SchoolOutlined } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Result from 'components/v2/common/Result';
import { ProfileFields } from './profileFields';
import * as S from '../ClientProfile.styled';
import { EducationData } from '../ClientProfile';

import useEnabledCountries from 'hooks/useEnabledCountries';
import { RootState } from 'redux/store';
import { isEducationRegionCountry, showHistoricalField } from 'utils/countries';

type EducationProps = {
    educationData: null | EducationData[];
    spouseEducationData: null | EducationData[];
    mobile: boolean;
    spouseOnly?: boolean;
};

const Education = ({
    educationData,
    spouseEducationData,
    mobile,
    spouseOnly = false,
}: EducationProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const { hasSelfServeMarket } = useEnabledCountries();
    const hasEducationData = !spouseOnly && !!educationData?.length;
    const hasSpouseEducationData = !!spouseEducationData?.length;
    function formatDate(date: Date | string | null) {
        if (date) {
            const stringDate = date.toString();
            const [year, month, day] = stringDate.split('-');
            const months =
                language === 'EN'
                    ? [
                          'January',
                          'February',
                          'March',
                          'April',
                          'May',
                          'June',
                          'July',
                          'August',
                          'September',
                          'October',
                          'November',
                          'December',
                      ]
                    : [
                          'Janvier',
                          'Février',
                          'Mars',
                          'Avril',
                          'Mai',
                          'Juin',
                          'Juillet',
                          'Août',
                          'Septembre',
                          'Octobre',
                          'Novembre',
                          'Décembre',
                      ];

            const monthName = months[parseInt(month, 10) - 1];
            const formattedDate = `${monthName} ${parseInt(day, 10)}, ${year}`;
            return formattedDate;
        } else {
            return '';
        }
    }

    return (
        <>
            {hasEducationData ? (
                <>
                    {hasSpouseEducationData && (
                        <S.Title>{t('applicant', { lng: language })}</S.Title>
                    )}

                    {educationData?.map((education: EducationData, index: number) => {
                        // Field of study is hidden for High school (same as form).
                        const showFieldOfStudy = education.type !== 'High school';
                        const extraFieldKeys = showFieldOfStudy
                            ? ['field_of_study_au', 'field_of_study_ca', 'field_of_study_uk']
                            : [];
                        const showRegion = isEducationRegionCountry(education.country);
                        const showLanguage = hasSelfServeMarket;
                        const showInstitution = showHistoricalField(
                            education.country,
                            ['AU', 'CA', 'UK'],
                            education.institution_name
                        );
                        const showPostcode = showHistoricalField(
                            education.country,
                            ['AU'],
                            education.zip
                        );

                        return (
                            <div key={index}>
                                {educationData && educationData.length > 1 && (
                                    <S.Title>{`${t('education', { lng: language })} ${
                                        index + 1
                                    }`}</S.Title>
                                )}
                                <S.InfoItem>
                                    <Tooltip title={t('type', { lng: language })}>
                                        <S.Label>{t('type', { lng: language })}</S.Label>
                                    </Tooltip>
                                    <Tooltip title={education.type}>
                                        <S.Info mobile={mobile}>{education.type}</S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                                <ProfileFields
                                    record={education}
                                    keys={extraFieldKeys}
                                    mobile={mobile}
                                />
                                {showPostcode && (
                                    <ProfileFields
                                        record={education}
                                        keys={['zip']}
                                        mobile={mobile}
                                    />
                                )}
                                <S.InfoItem>
                                    <Tooltip title={t('country', { lng: language })}>
                                        <S.Label>{t('country', { lng: language })}</S.Label>
                                    </Tooltip>
                                    <Tooltip title={education.country}>
                                        <S.Info mobile={mobile}>{education.country}</S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                                {showRegion && (
                                    <S.InfoItem>
                                        <Tooltip title={t('region', { lng: language })}>
                                            <S.Label>{t('region', { lng: language })}</S.Label>
                                        </Tooltip>
                                        <Tooltip title={education.region}>
                                            <S.Info mobile={mobile}>{education.region}</S.Info>
                                        </Tooltip>
                                    </S.InfoItem>
                                )}
                                {showLanguage && (
                                    <S.InfoItem>
                                        <Tooltip title={t('language', { lng: language })}>
                                            <S.Label>{t('language', { lng: language })}</S.Label>
                                        </Tooltip>
                                        <Tooltip title={education.language}>
                                            <S.Info mobile={mobile}>{education.language}</S.Info>
                                        </Tooltip>
                                    </S.InfoItem>
                                )}
                                {showInstitution && (
                                    <S.InfoItem>
                                        <Tooltip title={t('institution', { lng: language })}>
                                            <S.Label>{t('institution', { lng: language })}</S.Label>
                                        </Tooltip>
                                        <Tooltip title={education.institution_name}>
                                            <S.Info mobile={mobile}>
                                                {education.institution_name}
                                            </S.Info>
                                        </Tooltip>
                                    </S.InfoItem>
                                )}
                                <S.InfoItem>
                                    <Tooltip title={t('startDate', { lng: language })}>
                                        <S.Label>{t('startDate', { lng: language })}</S.Label>
                                    </Tooltip>
                                    <Tooltip title={formatDate(education.start)}>
                                        <S.Info mobile={mobile}>
                                            {formatDate(education.start)}
                                        </S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                                <S.InfoItem>
                                    <Tooltip title={t('ongoing', { lng: language })}>
                                        <S.Label>{t('ongoing', { lng: language })}</S.Label>
                                    </Tooltip>
                                    <Tooltip
                                        title={
                                            education.current
                                                ? t('yes', { lng: language })
                                                : t('no', { lng: language })
                                        }
                                    >
                                        <S.Info mobile={mobile}>
                                            {education?.current
                                                ? t('yes', { lng: language })
                                                : t('no', { lng: language })}
                                        </S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                                <S.InfoItem>
                                    <Tooltip title={t('endDate', { lng: language })}>
                                        <S.Label>{t('endDate', { lng: language })}</S.Label>
                                    </Tooltip>
                                    <Tooltip title={formatDate(education.end)}>
                                        <S.Info mobile={mobile}>{formatDate(education.end)}</S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                                <S.InfoItem>
                                    <Tooltip title={t('completed', { lng: language })}>
                                        <S.Label>{t('completed', { lng: language })}</S.Label>
                                    </Tooltip>
                                    <Tooltip
                                        title={
                                            education.completed
                                                ? t('yes', { lng: language })
                                                : t('no', { lng: language })
                                        }
                                    >
                                        <S.Info mobile={mobile}>
                                            {education.completed
                                                ? t('yes', { lng: language })
                                                : t('no', { lng: language })}
                                        </S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                            </div>
                        );
                    })}
                </>
            ) : !hasSpouseEducationData ? (
                <Result
                    icon={<SchoolOutlined sx={{ fontSize: 32 }} />}
                    title={t('noInfo', { lng: language })}
                    description="No education records have been added yet."
                />
            ) : null}
            {hasSpouseEducationData && (
                <>
                    {!spouseOnly && <S.Title>{t('partner', { lng: language })}</S.Title>}
                    {spouseEducationData?.map((education: EducationData, index: number) => (
                        <div key={index}>
                            {spouseEducationData && spouseEducationData.length > 1 && (
                                <S.Title>{`${t('education', { lng: language })} ${
                                    index + 1
                                }`}</S.Title>
                            )}
                            <S.InfoItem>
                                <Tooltip title={t('country', { lng: language })}>
                                    <S.Label>{t('country', { lng: language })}</S.Label>
                                </Tooltip>
                                <Tooltip title={education.country}>
                                    <S.Info mobile={mobile}>{education.country}</S.Info>
                                </Tooltip>
                            </S.InfoItem>
                            {/* Partner form shows region only after country is selected. */}
                            {!!education.country && (
                                <S.InfoItem>
                                    <Tooltip title={t('region', { lng: language })}>
                                        <S.Label>{t('region', { lng: language })}</S.Label>
                                    </Tooltip>
                                    <Tooltip title={education.region}>
                                        <S.Info mobile={mobile}>{education.region}</S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                            )}
                            <S.InfoItem>
                                <Tooltip title={t('programDurationInYears', { lng: language })}>
                                    <S.Label mobile={mobile}>
                                        {t('programDurationInYears', { lng: language })}
                                    </S.Label>
                                </Tooltip>
                                <Tooltip title={education.duration}>
                                    <S.Info mobile={mobile}>{education.duration}</S.Info>
                                </Tooltip>
                            </S.InfoItem>
                            <S.InfoItem>
                                <Tooltip title={t('type', { lng: language })}>
                                    <S.Label>{t('type', { lng: language })}</S.Label>
                                </Tooltip>
                                <Tooltip title={education.type}>
                                    <S.Info mobile={mobile}>{education.type}</S.Info>
                                </Tooltip>
                            </S.InfoItem>
                            <S.InfoItem>
                                <Tooltip title={t('startDate', { lng: language })}>
                                    <S.Label>{t('startDate', { lng: language })}</S.Label>
                                </Tooltip>
                                <Tooltip title={formatDate(education.start)}>
                                    <S.Info mobile={mobile}>{formatDate(education.start)}</S.Info>
                                </Tooltip>
                            </S.InfoItem>
                            <S.InfoItem>
                                <Tooltip title={t('ongoing', { lng: language })}>
                                    <S.Label>{t('ongoing', { lng: language })}</S.Label>
                                </Tooltip>
                                <Tooltip
                                    title={
                                        education.current
                                            ? t('yes', { lng: language })
                                            : t('no', { lng: language })
                                    }
                                >
                                    <S.Info mobile={mobile}>
                                        {education?.current
                                            ? t('yes', { lng: language })
                                            : t('no', { lng: language })}
                                    </S.Info>
                                </Tooltip>
                            </S.InfoItem>
                            <S.InfoItem>
                                <Tooltip title={t('endDate', { lng: language })}>
                                    <S.Label>{t('endDate', { lng: language })}</S.Label>
                                </Tooltip>
                                <Tooltip title={formatDate(education.end)}>
                                    <S.Info mobile={mobile}>{formatDate(education.end)}</S.Info>
                                </Tooltip>
                            </S.InfoItem>
                            <S.InfoItem>
                                <Tooltip title={t('completed', { lng: language })}>
                                    <S.Label>{t('completed', { lng: language })}</S.Label>
                                </Tooltip>
                                <Tooltip
                                    title={
                                        education.completed
                                            ? t('yes', { lng: language })
                                            : t('no', { lng: language })
                                    }
                                >
                                    <S.Info mobile={mobile}>
                                        {education.completed
                                            ? t('yes', { lng: language })
                                            : t('no', { lng: language })}
                                    </S.Info>
                                </Tooltip>
                            </S.InfoItem>
                        </div>
                    ))}
                </>
            )}
        </>
    );
};

export default Education;
